import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, Upload } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Checkbox } from "../../components/ui/checkbox";
import { CheckboxGroup, GroupField, ProductListField } from "../../components/FormFields";

export default function FormFill() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [files, setFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/business/forms/${id}`).then(({ data }) => setForm(data)).catch((e) => toast.error(apiError(e)));
  }, [id]);

  const setValue = (fid, v) => setValues((p) => ({ ...p, [fid]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("data", JSON.stringify(values));
      Object.values(files).flat().forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/business/forms/${id}/submit`, fd);
      toast.success("Submission created — verification email sent to the customer");
      navigate(`/dashboard/conversations/${data.conversation_id}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!form) return <p className="text-neutral-400" data-testid="fill-loading">Loading…</p>;

  return (
    <div className="max-w-2xl" data-testid="form-fill-page">
      <Link to="/dashboard/forms" className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900" data-testid="fill-back-link">
        <ArrowLeft className="h-4 w-4" /> Back to forms
      </Link>
      <h1 className="mt-4 text-3xl font-black">Fill: {form.title}</h1>
      <div className="mt-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-4" data-testid="assisted-notice">
        <ShieldCheck className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900">
          Assisted entry — you're completing this on a customer's behalf (e.g. a phone enquiry). The customer will
          receive an email asking them to <b>verify</b> their email address before the conversation is marked verified.
        </p>
      </div>

      <form onSubmit={submit} className="mt-8 space-y-6">
        {form.fields.map((field) => (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={`fill-${field.id}`}>
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </Label>
            {["text", "email", "phone", "number", "date"].includes(field.type) && (
              <Input
                id={`fill-${field.id}`}
                type={field.type === "phone" ? "tel" : field.type}
                placeholder={field.placeholder}
                value={values[field.id] || ""}
                onChange={(e) => setValue(field.id, e.target.value)}
                required={field.required}
                data-testid={`fill-field-${field.id}`}
              />
            )}
            {field.type === "textarea" && (
              <Textarea
                id={`fill-${field.id}`}
                placeholder={field.placeholder}
                value={values[field.id] || ""}
                onChange={(e) => setValue(field.id, e.target.value)}
                required={field.required}
                rows={4}
                data-testid={`fill-field-${field.id}`}
              />
            )}
            {field.type === "select" && (
              <Select value={values[field.id] || ""} onValueChange={(v) => setValue(field.id, v)}>
                <SelectTrigger data-testid={`fill-field-${field.id}`}>
                  <SelectValue placeholder={field.placeholder || "Choose…"} />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {(field.options || []).map((o) => (
                    <SelectItem key={o} value={o}>{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {field.type === "radio" && (
              <RadioGroup value={values[field.id] || ""} onValueChange={(v) => setValue(field.id, v)} data-testid={`fill-field-${field.id}`}>
                {(field.options || []).map((o) => (
                  <div key={o} className="flex items-center gap-3">
                    <RadioGroupItem value={o} id={`fill-${field.id}-${o}`} />
                    <Label htmlFor={`fill-${field.id}-${o}`} className="font-normal cursor-pointer">{o}</Label>
                  </div>
                ))}
              </RadioGroup>
            )}
            {field.type === "checkbox" && (field.options || []).length > 0 && (
              <CheckboxGroup
                field={field}
                value={values[field.id]}
                onChange={(v) => setValue(field.id, v)}
                testIdPrefix={`fill-field-${field.id}`}
              />
            )}
            {field.type === "group" && (
              <GroupField
                field={field}
                value={values[field.id]}
                onChange={(v) => setValue(field.id, v)}
                testIdPrefix={`fill-field-${field.id}`}
              />
            )}
            {field.type === "product_list" && (
              <ProductListField
                field={field}
                value={values[field.id]}
                onChange={(v) => setValue(field.id, v)}
                testIdPrefix={`fill-field-${field.id}`}
              />
            )}
            {field.type === "checkbox" && !(field.options || []).length && (
              <div className="flex items-center gap-3">
                <Checkbox
                  id={`fill-${field.id}`}
                  checked={!!values[field.id]}
                  onCheckedChange={(v) => setValue(field.id, !!v)}
                  data-testid={`fill-field-${field.id}`}
                />
                <Label htmlFor={`fill-${field.id}`} className="font-normal cursor-pointer">{field.placeholder || "Yes"}</Label>
              </div>
            )}
            {field.type === "file" && (
              <label className="flex items-center gap-3 bg-white border border-dashed border-neutral-300 rounded-xl px-4 py-4 cursor-pointer hover:border-neutral-500 transition-colors duration-150" data-testid={`fill-field-${field.id}`}>
                <Upload className="h-4 w-4 text-neutral-400" />
                <span className="text-sm text-neutral-500">
                  {files[field.id]?.length ? files[field.id].map((f) => f.name).join(", ") : field.placeholder || "Attach customer files"}
                </span>
                <input
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => setFiles((p) => ({ ...p, [field.id]: Array.from(e.target.files || []) }))}
                  data-testid={`fill-file-input-${field.id}`}
                />
              </label>
            )}
          </div>
        ))}
        <Button type="submit" disabled={submitting} className="rounded-full px-8 h-11" data-testid="fill-submit-button">
          {submitting ? "Submitting…" : "Submit & send verification email"}
        </Button>
      </form>
    </div>
  );
}
